// Per spec/components.md §big3-event-card (P-037.24 / D-132; reworked
// at P-037.26 / D-134). Fork of EventCard with the navy bar baked into
// the composite PNG instead of emitted as a separate `<tr>`. The
// composite (post-D-134) is a 190 × 150 PNG: the 40 px overhang region
// at the top hosts the upper half of the 100 × 100 circular logo, the
// 10 px navy bar at y = 40..50 marks the boundary line that the logo's
// vertical center sits on, and the 190 × 100 event image at y = 50..150
// fills the lower region (with the logo's bottom half overlapping it).
// Generated client-side by the form's composite-on-change orchestrator
// (`src/app/form/effects/big3-composite-orchestrator.tsx`) and uploaded
// to Firebase Storage; the resulting URL persists on the Block
// instance.
//
// **Two render arms:**
//
// 1. **Composite arm** (`compositeImageUrl` set, post-orchestrator):
//    single `<img>` at 190 × 150 with the 1 px IMG hairline border
//    (D-133). No separate navy-bar `<tr>` — the bar is baked into the
//    PNG.
//
// 2. **Fallback arm** (`compositeImageUrl` unset, pre-orchestrator or
//    on a never-edited card): same shape as regular EventCard — a 10 px
//    navy `<tr>` above a 190 × 100 image cell with `/fallback-image.png`.
//    No Big 3 logo overlay (the logo can only be baked in via the
//    composite) and no overhang region (the composite-arm overhang is
//    a logo-specific affordance — the fallback would render an empty
//    40 px gap above the navy bar). Visually equivalent to "a regular
//    event card without a logo," which is a clean degradation. The
//    fallback arm is intentionally shorter (190 × 110) than the
//    composite arm (190 × 150); mixed grids would briefly misalign
//    rows pre-orchestrator, but the orchestrator runs synchronously
//    after every image-source edit, so the steady-state grid is
//    all-composite + all-aligned.
//
// **Image-cell edge (P-037.29 / D-137 §item 4 revised):**
// - **Composite arm:** inset shadow is BAKED INTO the composite PNG
//   (on the event-image layer, before the logo overlay; see
//   `compositeBig3Image` step 2b in src/lib/image-composite.ts). No
//   cell-level box-shadow on the composite cell — the shadow is part
//   of the PNG content. IMG renders at full 190 × 150.
// - **Fallback arm:** cell-level `box-shadow: inset` stays (no PNG to
//   bake into); IMG renders at full 190 × 100. Same shadow CSS that
//   EventCard / SponsorCard use — progressive enhancement that's
//   mostly invisible behind the full-bleed fallback IMG, but renders
//   where the IMG has transparent edges. Same as the D-131 §13 state.
//
// All other rows (title / date+location / CTA / bottom spacer) match
// EventCard byte-for-byte. The mapping at the Block layer passes the
// same `ctaLabel`/`ctaUrl`/`linkColor` enum + dispatch semantics as
// event-card-group.
//
// **Why not extend EventCard with a `compositeImageUrl` prop?** Three
// reasons, in order of strength:
//
// 1. **The image-cell `<tr>` structure differs in row COUNT** — the
//    composite arm renders one `<tr>` (190 × 150 single img with the
//    D-133 hairline border); EventCard's image arm renders two `<tr>`s
//    (10 px navy bar + 190 × 100 image cell). A prop on EventCard
//    would force its image arm to branch on row-structure — turning a
//    one-shape Component into a two-shape Component for one
//    consumer's benefit. EventCard's post-D-130 contract is
//    intentionally single-shape; the navy-bar + image-cell pair is
//    its identity, not a variant.
//
// 2. **The Big 3 + composite identity belongs in its own Component.**
//    The Block layer's choice between composite-render and
//    fallback-render is a Big-3-specific concern — it doesn't apply
//    to event-card-group. Encoding the choice in EventCard exposes
//    the composite concept to every event-card-group consumer for no
//    benefit. The fork keeps the composite concept opaque to
//    consumers that don't need it.
//
// 3. **Future Big 3 evolution stays local.** If the chapter ever
//    wants a different overlay placement, a different composite
//    aspect, or a per-arm chapter badge, the change touches
//    Big3EventCard alone — event-card-group is shielded.
//
// The shared row patterns (title row, date/loc row, CTA dispatch,
// 16 px bottom spacer) are stable across both Cards and live
// identically here. The fork's cost is ~80 LOC of duplication; the
// benefit is two single-shape Components instead of one two-shape
// Component. Consistent with the SponsorCard fork at D-131 (which
// shares the title-row pattern with EventCard while diverging on
// the body shape — name vs. date/loc + CTA).

import { html } from '../render'
import { tokens } from '../tokens'
import { heading } from '../primitives/heading'
import { text } from '../primitives/text'
import { image } from '../primitives/image'
import { link } from '../primitives/link'
import { spacer } from '../primitives/spacer'
import { HtmlString } from '../types'
import { FALLBACK_IMAGE_SRC } from './helpers'

interface Big3EventCardBase {
  title: string
  dateText: string
  location?: string
  /** Composite PNG URL (190 × 110, navy bar + event image + Big 3 logo
   *  baked together). Set by the composite-on-change orchestrator after
   *  the staff editor sets either `imageUrl` or `big3LogoUrl`. Unset
   *  state renders the fallback arm (regular EventCard shape with
   *  `/fallback-image.png`, no logo overlay). */
  compositeImageUrl?: string
  imageAlt?: string
  /** Template-level link color from `RenderContext.linkColor` (D-118).
   *  Used as the link-arm CTA color. Defaults to
   *  `tokens.color['link-default']`. Text-arm CTA color is always
   *  black per D-117. */
  linkColor?: string
}

export type Big3EventCardProps =
  | (Big3EventCardBase & { ctaLabel?: undefined; ctaUrl?: undefined })
  | (Big3EventCardBase & { ctaLabel: string; ctaUrl?: string })

// P-037.26 D-134 — composite arm dimensions track image-composite.ts.
// Canvas is 190 × 150 (40 px overhang above the navy bar for the logo's
// upper half). Fallback arm shape stays 190 × 110 (10 px navy + 190 ×
// 100 fallback image, no logo overlay); fallback is the pre-orchestrator
// transient render only.
//
// P-037.29 D-137 §item 4 (revised at post-Phase-Close §item G) — IMG
// renders at full outer dimensions in both arms. Composite arm shadow
// is baked into the composite PNG via `applyInsetShadow` in image-
// composite.ts; fallback arm uses the pre-baked `/fallback-image.png`
// static asset. No cell-level CSS shadow needed — fully redundant.
const COMPOSITE_WIDTH = 190
const COMPOSITE_HEIGHT = 150
const FALLBACK_IMAGE_WIDTH = 190
const FALLBACK_IMAGE_HEIGHT = 100
const NAVY_BAR_HEIGHT = 10
const CARD_PAD_X = tokens.space[4]
const IMAGE_TO_TITLE_GAP = tokens.space[4]
const SECTION_GAP_14 = 14
const CTA_TEXT_COLOR = '#000000'
const NAVY = tokens.color['accent-navy-deep']
const BOTTOM_SPACER = spacer({ height: tokens.space[4] })

export function big3EventCard(props: Big3EventCardProps): HtmlString {
  // Image-cell arm dispatch. Composite arm: single 190 × 110 img with
  // navy bar baked in. Fallback arm: 10 px navy `<tr>` + 190 × 100
  // fallback-image cell (same as EventCard's fallback shape).
  const imageRows =
    props.compositeImageUrl !== undefined
      ? compositeImageRows(props.compositeImageUrl, props.imageAlt ?? props.title)
      : fallbackImageRows()
  // 16 px bold navy title. heading() defaults to `accent-navy-deep`
  // post-D-118; passed explicitly for readability at this non-default
  // 16 px size.
  const titleHtml = heading({
    text: props.title,
    level: 3,
    size: tokens.type.size.base,
    weight: tokens.type.weight.bold,
    color: NAVY,
  })
  // Date + optional location as a single 14 px text block with `<br>`
  // separator. Identical to EventCard's date/loc row.
  // prettier-ignore
  const dateLocText: string | HtmlString =
    props.location !== undefined ? html`${props.dateText}<br>${props.location}` : props.dateText
  // P-037.31 third post-Phase-Close §B — date+location dropped 14 → 12 px
  // (`sm` → `xs`); mirrors the EventCard change so both Big-3 arms
  // (big3Events composite cards via Big3EventCard + signatureEvents
  // regular cards via EventCard) share the same date/location size.
  const dateLocHtml = text({ text: dateLocText, size: tokens.type.size.xs })
  // CTA dispatch — three arms by (ctaLabel, ctaUrl) presence, identical
  // to EventCard's CTA dispatch (D-130).
  let ctaHtml: HtmlString | undefined
  if (props.ctaLabel !== undefined) {
    const ctaLinkColor = props.linkColor ?? tokens.color['link-default']
    ctaHtml =
      props.ctaUrl !== undefined
        ? text({
            text: link({
              text: props.ctaLabel,
              href: props.ctaUrl,
              color: ctaLinkColor,
              weight: tokens.type.weight.bold,
            }),
          })
        : text({
            text: props.ctaLabel,
            weight: tokens.type.weight.bold,
            color: CTA_TEXT_COLOR,
          })
  }
  // prettier-ignore
  const ctaRow = ctaHtml !== undefined
    ? html`<tr><td style="padding: ${SECTION_GAP_14}px ${CARD_PAD_X}px 0;">${ctaHtml}</td></tr>`
    : undefined

  // P-048.6 §B / D-149 — outer card table fixed at FALLBACK_IMAGE_WIDTH
  // (190) + centered. See `event-card.ts` for the full rationale.
  // Constant choice: `FALLBACK_IMAGE_WIDTH` (190) matches `COMPOSITE_WIDTH`
  // (also 190); the composite arm renders a 190 × 150 PNG and the
  // fallback arm renders a 190 × 100 image — both arms share the same
  // outer card width.
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${FALLBACK_IMAGE_WIDTH}" align="center" style="margin: 0 auto;">${imageRows}<tr><td style="padding: ${IMAGE_TO_TITLE_GAP}px ${CARD_PAD_X}px 0;">${titleHtml}</td></tr><tr><td style="padding: ${SECTION_GAP_14}px ${CARD_PAD_X}px 0;">${dateLocHtml}</td></tr>${ctaRow}${BOTTOM_SPACER}</table>`
}

function compositeImageRows(src: string, alt: string): HtmlString {
  const composite = image({
    src,
    alt,
    width: COMPOSITE_WIDTH,
    height: COMPOSITE_HEIGHT,
    align: 'center',
  })
  // No cell-level box-shadow — the inset shadow is baked into the
  // composite PNG itself (D-137 §item 4 revised).
  // prettier-ignore
  return html`<tr><td>${composite}</td></tr>`
}

function fallbackImageRows(): HtmlString {
  // Same 10 px navy bar + 190 × 100 fallback-image shape as EventCard's
  // post-D-131 fallback arm. The card visually reads as a "regular event
  // card without a logo" — clean degradation pre-composite. Cell-level
  // `box-shadow: inset` stays (no baked-in shadow on the chapter
  // fallback PNG); progressive-enhancement that's mostly invisible
  // behind the full-bleed fallback IMG.
  // P-048.5 / D-148 — wrap the bar in a 190px-wide centered inner table
  // to match the fallback image width on mobile collapse. See
  // `event-card.ts` navyBarRow comment for the full rationale + the 3-
  // layer belt-and-suspenders centering pattern.
  // prettier-ignore
  const navyBarRow = html`<tr><td align="center" style="padding: 0; font-size: 0; line-height: 0;"><table role="presentation" width="${FALLBACK_IMAGE_WIDTH}" cellpadding="0" cellspacing="0" border="0" align="center" style="margin: 0 auto;"><tr><td height="${NAVY_BAR_HEIGHT}" bgcolor="${NAVY}" style="line-height: ${NAVY_BAR_HEIGHT}px; font-size: 0;">&nbsp;</td></tr></table></td></tr>`
  const fallback = image({
    src: FALLBACK_IMAGE_SRC,
    alt: '',
    width: FALLBACK_IMAGE_WIDTH,
    height: FALLBACK_IMAGE_HEIGHT,
    align: 'center',
  })
  // prettier-ignore
  return html`${navyBarRow}<tr><td>${fallback}</td></tr>`
}
